import { toZonedTime } from "date-fns-tz";

/**
 * Extracts the date portion (YYYY-MM-DD) from an ISO datetime string.
 * This ignores timezone and time components, treating the date as a calendar date.
 *
 * @param isoString - ISO datetime string like "2025-11-18T00:00:00.000Z"
 * @returns Date string in YYYY-MM-DD format, or empty string if invalid
 *
 * @example
 * extractDateFromISO("2025-11-18T00:00:00.000Z") // "2025-11-18"
 * extractDateFromISO("2025-11-18T23:59:59.999Z") // "2025-11-18"
 * extractDateFromISO(undefined) // ""
 */
export const extractDateFromISO = (
  isoString: string | null | undefined,
): string => {
  if (!isoString) return "";

  // Extract YYYY-MM-DD from ISO string
  const dateMatch = isoString.match(/^\d{4}-\d{2}-\d{2}/);
  return dateMatch ? dateMatch[0] : "";
};

/**
 * Formats a date for HTML date input (YYYY-MM-DD format).
 * If input is an ISO string, extracts date directly to avoid timezone issues.
 * If input is a Date object, formats using local date components.
 *
 * @param date - Date as ISO string, Date object, or null/undefined
 * @returns Date string in YYYY-MM-DD format, or empty string if invalid
 *
 * @example
 * formatDateForInput("2025-11-18T00:00:00.000Z") // "2025-11-18"
 * formatDateForInput(new Date(2025, 10, 18)) // "2025-11-18"
 * formatDateForInput(null) // ""
 */
export const formatDateForInput = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "";

  // If it's an ISO string, extract date directly to avoid timezone issues
  if (typeof date === "string") {
    return extractDateFromISO(date);
  }

  // If it's a Date object, format it using local components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Compares two date strings and returns the later one.
 * Works with YYYY-MM-DD format strings.
 *
 * @param date1 - First date string in YYYY-MM-DD format
 * @param date2 - Second date string in YYYY-MM-DD format
 * @returns The later date string
 *
 * @example
 * getMaxDate("2025-11-18", "2025-12-01") // "2025-12-01"
 */
export const getMaxDate = (date1: string, date2: string): string => {
  return date1 > date2 ? date1 : date2;
};

/**
 * Converts a UTC time to a specific timezone.
 *
 * @param value - UTC date as Date, string, or null
 * @param timezone - Target timezone (e.g., 'America/New_York'). Uses browser's timezone if not specified.
 * @returns Date in the specified timezone, or null if value is empty
 *
 * @example
 * // If UTC time is "2025-01-05T15:00:00Z"
 * // and timezone is "America/New_York" (EST, -5 hours)
 * // returns Date representing "2025-01-05T10:00:00-05:00"
 */
export const utcToZonedTime = (
  value: Date | string | null,
  timezone?: string,
): Date | null => {
  if (!value) return null;
  const targetTimezone =
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return toZonedTime(value, targetTimezone);
};
